import { MeshBuilder, Vector3, type Mesh } from "@babylonjs/core";
import { Btn, HeroId, Slot, fxk, hv, pk } from "@rivalwatch/shared";
import { baked } from "../../audio/bake.ts";
import { bell, click, drive, fm, later, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import { glow } from "../../game/materials.ts";
import type { ClientHero } from "../types.ts";

const PK_SHURIKEN = pk(HeroId.Psyclone, 0);
const FX_BUTTERFLY = fxk(HeroId.Psyclone, 0);
const hz = (semi: number) => 293.66 * 2 ** (semi / 12);
const theme = {
  bpm: 132,
  octave: 1,
  voice: bell,
  notes: [
    [12, 0.25],
    [11, 0.25],
    [7, 0.5],
    [4, 0.5],
    [9, 0.75],
  ],
} satisfies ClientHero["audio"]["theme"];

const mindbowCue = baked("fire:psyclone:mindbow", () =>
  trim(14.8, (o) => {
    click(o, 0.18, 5200, 0.003);
    fm(o, { f: hz(24), ratio: 1.5, index: 2.4, index1: 0.2, gain: 0.08, d: 0.09 });
    noise(o, { color: "white", type: "bandpass", f: 3200, f1: 1400, q: 1.3, gain: 0.18, d: 0.06 });
    return 0.18;
  }), { variants: 5, jitter: 0.012 });

const wingPinsCue = baked("ability:psyclone:wing-pins", () =>
  trim(10.5, (o) => {
    for (let i = 0; i < 5; i++) {
      click(later(o, i * 0.025), 0.035, 4500 + i * 220, 0.003);
      whoosh(later(o, i * 0.025), 0.38, 1200, 3600, 0.08);
    }
    tone(o, { f: hz(19), f1: hz(31), glide: 0.24, gain: 0.12, d: 0.28 });
    return 0.46;
  }), { variants: 3, jitter: 0.01 });

const stealthCue = baked("ability:psyclone:quiet-weather", () =>
  trim(12.7, (o) => {
    noise(o, { color: "pink", type: "highpass", f: 1600, f1: 5200, gain: 0.22, a: 0.04, d: 0.45 });
    fm(later(o, 0.04), { f: hz(12), ratio: 2.01, index: 2.2, index1: 0, gain: 0.08, d: 0.32 });
    return 0.7;
  }), { variants: 3, jitter: 0.01 });

const galeBladeCue = baked("ability:psyclone:gale-blade", () =>
  trim(11.6, (o) => {
    whoosh(o, 0.6, 900, 5200, 0.18, 0, 1.5);
    click(later(o, 0.1), 0.28, 4800, 0.004);
    thump(later(o, 0.12), 0.22, 180, 80, 0.1);
    return 0.42;
  }), { variants: 3, jitter: 0.01 });

const butterflyCue = baked("ability:psyclone:butterfly-spiral", () =>
  trim(1.1, (o) => {
    tone(o, { type: "triangle", f: hz(12), f1: hz(24), glide: 0.5, gain: 0.08, a: 0.08, d: 0.55 });
    noise(drive(o, 1.8), { color: "white", type: "bandpass", f: 900, f1: 4200, q: 0.8, gain: 0.45, a: 0.12, d: 0.55 });
    for (let i = 0; i < 4; i++) fm(later(o, 0.12 + i * 0.08), { f: hz(24 + i * 2), ratio: 1.33, index: 2, index1: 0, gain: 0.055, d: 0.18 });
    return 1.1;
  }), { variants: 2, jitter: 0.005 });

const reloadCue = (seconds: number) => baked("reload:psyclone", () =>
  trim(15, (o) => {
    click(o, 0.08, 2600, 0.005, seconds * 0.25);
    tone(later(o, seconds * 0.35), { type: "triangle", f: hz(19), f1: hz(31), glide: seconds * 0.35, gain: 0.035, d: seconds * 0.45 });
    click(o, 0.09, 4200, 0.004, seconds * 0.78);
    return seconds + 0.2;
  }), { variants: 2, jitter: 0.01 });

type PsycloneGear = { bowL: Mesh; bowR: Mesh; sash: Mesh; wingL: Mesh; wingR: Mesh; mask: Mesh; visor: Mesh };

const hero: ClientHero<PsycloneGear> = {
  id: HeroId.Psyclone,
  bulk: 0.86,
  shape: { head: 0.9, torso: 0.9, arms: 0.95, legs: 1.08 },
  gear(g) {
    const { scene, rig, add, glow: glowMat, toon: toonMat, R } = g;
    const bowL = add(MeshBuilder.CreateBox("mindbow-l", { width: 0.07, height: 0.12, depth: 0.52 }, scene), rig.handL, glowMat("#ff9cf5"));
    const bowR = add(MeshBuilder.CreateBox("mindbow-r", { width: 0.07, height: 0.12, depth: 0.52 }, scene), rig.handR, glowMat("#ff9cf5"));
    bowL.position.z = bowR.position.z = -0.28;
    const stringL = add(MeshBuilder.CreateBox("mindbow-string-l", { width: 0.018, height: 0.5, depth: 0.018 }, scene), rig.handL, glowMat("#ffe7fb"));
    const stringR = add(MeshBuilder.CreateBox("mindbow-string-r", { width: 0.018, height: 0.5, depth: 0.018 }, scene), rig.handR, glowMat("#ffe7fb"));
    stringL.position.z = stringR.position.z = -0.28;
    const sash = add(MeshBuilder.CreateTorus("psi-sash", { diameter: R * 1.8, thickness: 0.03, tessellation: 36 }, scene), rig.torso, toonMat("#b04ad8"));
    sash.rotation.x = Math.PI / 2;
    const wingL = add(MeshBuilder.CreateBox("wing-left", { width: 0.08, height: 0.32, depth: 0.9 }, scene), rig.torso, glowMat("#ff9cf5", 0.6));
    const wingR = add(MeshBuilder.CreateBox("wing-right", { width: 0.08, height: 0.32, depth: 0.9 }, scene), rig.torso, glowMat("#ff9cf5", 0.6));
    wingL.position.set(-R * 0.75, 0.2, R * 0.75);
    wingR.position.set(R * 0.75, 0.2, R * 0.75);
    wingL.rotation.z = -0.45;
    wingR.rotation.z = 0.45;
    const mask = add(MeshBuilder.CreateBox("ninja-mask", { width: R * 0.9, height: 0.18, depth: 0.04 }, scene), rig.head, toonMat("#160924"));
    mask.position.set(0, 0.03, -R * 0.52);
    const visor = add(MeshBuilder.CreateBox("psychic-visor", { width: R * 0.68, height: 0.055, depth: 0.05 }, scene), rig.head, glowMat("#ff65f2"));
    visor.position.set(0, 0.08, -R * 0.56);
    return { bowL, bowR, sash, wingL, wingR, mask, visor };
  },
  animate(gear, f, rig) {
    const stealth = f.v[0] > 0.01;
    const ult = f.ultActive || f.v[2] > 0.01;
    for (const p of Object.values(rig.parts)) if (p) p.visibility = stealth ? 0.35 : 1;
    gear.sash.rotation.y = f.t * (ult ? 10 : 2);
    gear.wingL.rotation.y = Math.sin(f.t * 8) * 0.35;
    gear.wingR.rotation.y = -Math.sin(f.t * 8) * 0.35;
    gear.wingL.setEnabled(f.v[1] > 0 || ult);
    gear.wingR.setEnabled(f.v[1] > 0 || ult);
    gear.visor.scaling.x = 1 + Math.sin(f.t * 8) * 0.06 + (ult ? 0.35 : 0);
    if (ult) return "attack-kick-right";
    if (f.action === 1) return "holding-both-shoot";
    if (f.action === 2 || f.action === 4) return "interact-left";
    return "holding-both";
  },
  hud(c, eid) {
    return [
      { label: "Stealth", value: hv(c, eid, 0), color: "#b04ad8" },
      { label: "Shuriken recall", value: hv(c, eid, 1) / 3, color: "#ff9cf5" },
    ];
  },
  lifting(_c, _eid, buttons, grounded) {
    return !grounded && !!(buttons & Btn.Jump);
  },
  projectiles: {
    [PK_SHURIKEN]: (scene) => {
      const s = MeshBuilder.CreateCylinder("psi-star", { diameter: 0.28, height: 0.035, tessellation: 5 }, scene);
      s.material = glow(scene, "#ff65f2", 0.9);
      return {
        gear: s,
        update(_m, fr) {
          s.position.set(fr.pos.x, fr.pos.y, fr.pos.z);
          s.rotation.y = Math.atan2(-fr.dir.x, -fr.dir.z);
          s.rotation.z += fr.dt * 22;
        },
        dispose: () => s.dispose(),
      };
    },
  },
  fx: {
    [FX_BUTTERFLY]: {
      explosion(c, pos, radius) {
        const ring = MeshBuilder.CreateTorus("butterfly-spiral-ring", { diameter: radius * 1.8, thickness: 0.045, tessellation: 56 }, c.scene);
        ring.position.copyFrom(new Vector3(pos.x, pos.y + 0.12, pos.z));
        ring.rotation.x = Math.PI / 2;
        ring.material = c.glow("#ff65f2", 0.65);
        const bladeA = MeshBuilder.CreateBox("butterfly-blade-a", { width: radius * 0.12, height: 0.04, depth: radius * 1.4 }, c.scene);
        const bladeB = MeshBuilder.CreateBox("butterfly-blade-b", { width: radius * 0.12, height: 0.04, depth: radius * 1.4 }, c.scene);
        bladeA.material = bladeB.material = c.glow("#ffe7fb", 0.75);
        c.timed(ring, 0.45, (t, m) => {
          m.scaling.setAll(0.35 + t * 0.9);
          m.rotation.z = t * Math.PI * 2;
          m.visibility = 1 - t;
        });
        for (const [i, blade] of [bladeA, bladeB].entries()) {
          blade.position.copyFrom(new Vector3(pos.x, pos.y + 0.35 + i * 0.08, pos.z));
          c.timed(blade, 0.35, (t, m) => {
            m.rotation.y = t * Math.PI * 4 + i * Math.PI / 2;
            m.visibility = 1 - t;
          });
        }
      },
    },
  },
  audio: {
    weight: "light",
    fire: () => ({ key: "fire:psyclone:mindbow", cue: mindbowCue }),
    reload: reloadCue,
    ability: {
      [Slot.Secondary]: wingPinsCue,
      [Slot.Ability1]: galeBladeCue,
      [Slot.Ability2]: stealthCue,
      [Slot.Ultimate]: butterflyCue,
    },
    theme,
    themeTrim: { heroic: 1.7, hostile: -6.2, tease: 3.6 },
  },
  icons: {
    [Slot.Primary]: "M4 12 H13 M13 12 L20 7 M13 12 L20 17 M6 8 L6 16",
    [Slot.Secondary]: ["M12 4 L14 10 L20 12 L14 14 L12 20 L10 14 L4 12 L10 10 Z", "M12 8 V16 M8 12 H16"],
    [Slot.Ability1]: "M4 18 L20 6 M13 5 L20 6 L18 13",
    [Slot.Ability2]: "M4 16 C8 8 16 8 20 16 M7 17 C10 14 14 14 17 17",
    [Slot.Ultimate]: "M12 12 C6 5 3 7 5 13 C6 18 10 16 12 12 C14 16 18 18 19 13 C21 7 18 5 12 12 Z",
  },
};

export default hero;
